<template>
  <div :class="width">
    <label v-if="label" :for="id" class="block text-sm font-medium text-gray-700 mb-2">
      {{ label }}
    </label>
    <Listbox
      :modelValue="modelValue"
      @update:modelValue="$emit('update:modelValue', $event)"
      :multiple="multiple"
      :disabled="disabled"
    >
      <div class="relative">
        <ListboxButton
          :id="id"
          class="relative w-full cursor-pointer rounded-md border bg-white pl-3 pr-10 text-left text-sm focus:outline-none focus:ring-1 focus:ring-main transition-colors min-h-10"
          :class="[
            multiple ? 'py-0.5' : 'py-1.5',
            error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-main',
            disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'hover:border-gray-400',
          ]"
        >
          <span v-if="!hasValue" class="block truncate text-gray-400 py-0.5">
            {{ placeholder || label }}
          </span>

          <div v-else-if="multiple && Array.isArray(modelValue)" class="flex flex-wrap gap-1.5">
            <span
              v-for="(item, idx) in selectedOptions"
              :key="idx"
              class="inline-flex items-center rounded-md bg-gray-100 px-2 py-1.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-500/10"
              @click.stop
            >
              {{ item.label }}
              <button
                type="button"
                class="ml-1 inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-500 focus:outline-none"
                @click.stop="removeOption(item.value)"
              >
                <span class="sr-only">Remove {{ item.label }}</span>
                <Icon icon="heroicons:x-mark-20-solid" class="h-3 w-3" />
              </button>
            </span>
          </div>

          <span v-else class="block truncate py-0.5">
            {{ displayValue }}
          </span>

          <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <Icon
              icon="heroicons:chevron-up-down-20-solid"
              class="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </ListboxButton>

        <transition
          leave-active-class="transition duration-100 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <ListboxOptions
            class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none"
          >
            <ListboxOption
              v-slot="{ active, selected }"
              v-for="(option, index) in options"
              :key="index"
              :value="getValue ? getValue(option) : option"
              as="template"
            >
              <li
                :class="[
                  active ? 'bg-main/10 text-main' : 'text-gray-900',
                  'relative cursor-pointer select-none py-2 pl-10 pr-4',
                ]"
              >
                <span :class="[selected ? 'font-medium' : 'font-normal', 'block truncate']">
                  {{ getLabel ? getLabel(option) : option }}
                </span>
                <span
                  v-if="selected"
                  class="absolute inset-y-0 left-0 flex items-center pl-3 text-main"
                >
                  <Icon icon="heroicons:check-20-solid" class="h-5 w-5" aria-hidden="true" />
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>
    <p v-if="error" class="mt-1.5 text-xs text-red-600">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed } from 'vue'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { Icon } from '@iconify/vue'

interface Props {
  id?: string
  label?: string
  modelValue?: any
  options: any[]
  placeholder?: string
  error?: string
  width?: string
  multiple?: boolean
  disabled?: boolean
  getLabel?: (option: any) => string
  getValue?: (option: any) => any
}

const props = withDefaults(defineProps<Props>(), {
  width: 'w-full',
  multiple: false,
  disabled: false,
})

const emit = defineEmits(['update:modelValue'])

const hasValue = computed(() => {
  if (props.multiple) {
    return Array.isArray(props.modelValue) && props.modelValue.length > 0
  }
  return props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== ''
})

const selectedOptions = computed(() => {
  if (!props.multiple || !Array.isArray(props.modelValue)) return []

  return props.modelValue.map((val: any) => {
    const option = props.options.find((opt) =>
      props.getValue ? props.getValue(opt) === val : opt === val,
    )
    return {
      value: val,
      label: option ? (props.getLabel ? props.getLabel(option) : option) : val,
    }
  })
})

const removeOption = (valueToRemove: any) => {
  if (Array.isArray(props.modelValue)) {
    const newValue = props.modelValue.filter((v: any) => v !== valueToRemove)
    if (newValue.length === 0) {
      emit('update:modelValue', undefined)
    } else {
      emit('update:modelValue', newValue)
    }
  }
}

const displayValue = computed(() => {
  if (!hasValue.value) return ''

  const option = props.options.find((opt) =>
    props.getValue ? props.getValue(opt) === props.modelValue : opt === props.modelValue,
  )
  return option ? (props.getLabel ? props.getLabel(option) : option) : props.modelValue
})
</script>
